/**
 * @file: useLeadMonthlyNewCount.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The "本月新增" KPI: leads CREATED within the current calendar month. This
 * count is fixed to this month and is intentionally independent of the page's
 * status / target-date / keyword filters, so it queries `/leads/stats` with
 * only a `createdAt` month range and reads back `.total`.
 */

import { LeadListFilter } from '@euler/model/lead/types';
import { useServiceFactory } from '@euler/services/factory';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import { useEffect, useMemo, useState } from 'react';

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

export const useLeadMonthlyNewCount = (
  // Bumped by the caller (the refresh control) to recount; the month bounds
  // never change on their own, so nothing else would retrigger the fetch.
  reloadKey?: number,
): number | undefined => {
  const { leadService } = useServiceFactory();
  const [count, setCount] = useState<number | undefined>(undefined);

  // Current calendar month bounds, both inclusive. Recomputed only when the
  // month string flips (cheap, and avoids a fresh fetch on every render).
  const filter = useMemo<LeadListFilter>(() => {
    const now = new Date();
    return {
      createdAt: {
        start: fmt(startOfMonth(now)),
        end: fmt(endOfMonth(now)),
        startInclusive: true,
        endInclusive: true,
      },
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await leadService.stats(filter);
        if (!cancelled) setCount(next.total);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadService, filter, reloadKey]);

  return count;
};
